import { useEffect, useRef } from "react";

// A barcode scanner types the code as a burst of fast keystrokes (well under 80ms
// apart, far faster than a person) and ends with Enter, some with Tab (or both).
// While enabled, catch that burst anywhere on the page and hand the code to
// `onScan`. If the cursor was sitting in a field, the code got typed into it too,
// so it is removed from that field again, and a focused button is blurred so the
// terminator key cannot trigger it.
const MAX_GAP = 80;

const stripFromField = (target: HTMLElement | null, code: string) => {
  const field = target as HTMLInputElement | HTMLTextAreaElement | null;
  if (!field || (field.tagName !== "INPUT" && field.tagName !== "TEXTAREA") || !field.value.endsWith(code)) return;
  const next = field.value.slice(0, -code.length);
  // React listens to the native value setter, so set it that way and fire input.
  const proto = field.tagName === "INPUT" ? window.HTMLInputElement.prototype : window.HTMLTextAreaElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, "value")?.set;
  if (setter) setter.call(field, next);
  else field.value = next;
  field.dispatchEvent(new Event("input", { bubbles: true }));
};

export const useBarcodeScanner = (onScan: (code: string) => void, enabled: boolean = true) => {
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;

  useEffect(() => {
    if (!enabled) return;
    let buffer = "";
    let lastAt = 0;

    const onKeyDown = (e: KeyboardEvent) => {
      const now = Date.now();
      // A slow key (a person typing) starts the buffer over, so only a fast run counts.
      if (now - lastAt > MAX_GAP) buffer = "";
      lastAt = now;
      if (e.key === "Enter" || e.key === "Tab") {
        const code = buffer.trim();
        buffer = "";
        if (code.length >= 3) {
          e.preventDefault();
          e.stopPropagation();
          stripFromField(e.target as HTMLElement | null, code);
          const active = document.activeElement as HTMLElement | null;
          if (active && (active.tagName === "BUTTON" || active.tagName === "A")) active.blur();
          onScanRef.current(code);
        }
        return;
      }
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) buffer += e.key;
    };
    // Capture phase, so the Enter is swallowed before a form or field acts on it.
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [enabled]);
};

export default useBarcodeScanner;
